import { Injectable } from "@angular/core";
import { Application, ApplicationData } from "../interfaces/application";
import { map, Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";

@Injectable()
export class ApplicationService extends ApplicationData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'job-applications';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?with=deep,2`);
  }

  listAll(): Observable<Application[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('populate', '*');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<Application[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<Application[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<Application> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(Application: Application): Observable<Application> {
    let params: HttpParams = new HttpParams()
    return this.api.post(`${this.url}?populate=*`, { data: Application }, params)
      .pipe(map(res => res.data));
  }

  update(Application: Application): Observable<Application> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    const changedApplication = omit(Application, ['id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']);
    return this.api.put(`${this.url}/${Application.documentId}?populate=*`, { data: changedApplication }, params)
      .pipe(map(res => res.data));
  }

  archive(Application: Application, date: Date, user: User): Observable<Application> {
    return this.api.put(`${this.url}/${Application.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(Application: Application): Observable<Application> {
    return this.api.put(`${this.url}/${Application.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }

}